import { useCallback, useMemo, useState } from 'react';

type UseRemixThinkingBarProgressOptions = {
	onOpenPreview: () => void;
};

type UseRemixThinkingBarProgressResult = {
	showProgressBar: boolean;
	isComplete: boolean;
	onCompleteAnimationFinished: () => void;
	/** Call when the SSE `ACTION_COMPLETE` event fires. */
	signalActionReady: () => void;
	/** Call when a new remix flow starts to clear stale completion state. */
	resetCompletion: () => void;
};

/**
 * Wiring for the remix thinking bar's loading progress bar.
 * The preview modal opens after the bar fills to 100%.
 */
export const useRemixThinkingBarProgress = ({
	onOpenPreview,
}: UseRemixThinkingBarProgressOptions): UseRemixThinkingBarProgressResult => {
	const [isComplete, setIsComplete] = useState(false);

	const signalActionReady = useCallback(() => {
		// Defer opening the preview until the progress bar fills to 100%.
		setIsComplete(true);
	}, []);

	const resetCompletion = useCallback(() => {
		setIsComplete(false);
	}, []);

	return useMemo(
		() => ({
			showProgressBar: true,
			isComplete,
			onCompleteAnimationFinished: onOpenPreview,
			signalActionReady,
			resetCompletion,
		}),
		[isComplete, onOpenPreview, signalActionReady, resetCompletion],
	);
};
